import api from './api';

/**
 * Course Service
 *
 * Handles all course-related API calls including:
 * - Public course browsing
 * - Admin course management (CRUD)
 * - Module and lesson management
 * - PDF handling
 */

const courseService = {
  // ===== PUBLIC ENDPOINTS =====

  /**
   * Get all published courses (public access)
   * @returns {Promise<Array>} List of courses with module_count and lesson_count
   */
  getAllCourses: async () => {
    const response = await api.get('/courses/');
    return response.data;
  },

  /**
   * Get course details by ID (public access)
   * Includes nested modules and lessons
   * @param {number} id - Course ID
   * @returns {Promise<Object>} Course with modules and lessons
   */
  getCourseById: async (id) => {
    const response = await api.get(`/courses/${id}/`);
    return response.data;
  },

  // ===== ADMIN COURSE MANAGEMENT =====

  /**
   * Create a new course (Admin only)
   * @param {Object} courseData - Course data
   * @param {File} [courseData.image] - Course thumbnail image file
   * @returns {Promise<Object>} Created course
   */
  createCourse: async (courseData) => {
    const formData = new FormData();

    // Append all course fields
    Object.keys(courseData).forEach(key => {
      if (courseData[key] !== null && courseData[key] !== undefined) {
        formData.append(key, courseData[key]);
      }
    });

    const response = await api.post('/courses/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Update an existing course (Admin only)
   * @param {number} id - Course ID
   * @param {Object} courseData - Updated course data
   * @param {File} [courseData.image] - New course thumbnail (optional)
   * @returns {Promise<Object>} Updated course
   */
  updateCourse: async (id, courseData) => {
    const formData = new FormData();

    // Append all course fields
    Object.keys(courseData).forEach(key => {
      if (courseData[key] !== null && courseData[key] !== undefined) {
        formData.append(key, courseData[key]);
      }
    });

    const response = await api.put(`/courses/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Delete a course (Admin only)
   * @param {number} id - Course ID
   * @returns {Promise<void>}
   */
  deleteCourse: async (id) => {
    await api.delete(`/courses/${id}/`);
  },

  // ===== ADMIN MODULE MANAGEMENT =====

  /**
   * Get all modules for a course (Admin only)
   * @param {number} courseId - Course ID
   * @returns {Promise<Array>} List of modules
   */
  getModulesByCourse: async (courseId) => {
    const response = await api.get(`/modules/?course_id=${courseId}`);
    return response.data;
  },

  /**
   * Create a new module (Admin only)
   * @param {Object} moduleData - Module data
   * @param {number} moduleData.course_id - Parent course ID
   * @param {string} moduleData.title - Module title
   * @param {number} [moduleData.order] - Module order (optional, auto-assigned if not provided)
   * @returns {Promise<Object>} Created module
   */
  createModule: async (moduleData) => {
    const response = await api.post('/modules/', moduleData);
    return response.data;
  },

  /**
   * Update an existing module (Admin only)
   * @param {number} id - Module ID
   * @param {Object} moduleData - Updated module data
   * @returns {Promise<Object>} Updated module
   */
  updateModule: async (id, moduleData) => {
    const response = await api.put(`/modules/${id}/`, moduleData);
    return response.data;
  },

  /**
   * Delete a module (Admin only)
   * @param {number} id - Module ID
   * @returns {Promise<void>}
   */
  deleteModule: async (id) => {
    await api.delete(`/modules/${id}/`);
  },

  /**
   * Reorder modules in bulk (Admin only)
   * @param {Array<number>} moduleIds - Array of module IDs in desired order
   * @returns {Promise<Object>} Success message
   */
  reorderModules: async (moduleIds) => {
    const response = await api.post('/modules/reorder/', { module_ids: moduleIds });
    return response.data;
  },

  // ===== ADMIN LESSON MANAGEMENT =====

  /**
   * Get all lessons for a module (Admin only)
   * @param {number} moduleId - Module ID
   * @returns {Promise<Array>} List of lessons
   */
  getLessonsByModule: async (moduleId) => {
    const response = await api.get(`/lessons/?module_id=${moduleId}`);
    return response.data;
  },

  /**
   * Create a new lesson (Admin only)
   * @param {Object} lessonData - Lesson data
   * @param {number} lessonData.module_id - Parent module ID
   * @param {string} lessonData.title - Lesson title
   * @param {File} [lessonData.pdf_file] - Lesson PDF file
   * @param {boolean} [lessonData.is_preview] - Whether lesson is free preview
   * @param {string} [lessonData.duration_estimate] - Estimated duration
   * @returns {Promise<Object>} Created lesson
   */
  createLesson: async (lessonData) => {
    const formData = new FormData();

    // Append all lesson fields
    Object.keys(lessonData).forEach(key => {
      if (lessonData[key] !== null && lessonData[key] !== undefined) {
        formData.append(key, lessonData[key]);
      }
    });

    const response = await api.post('/lessons/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Update an existing lesson (Admin only)
   * @param {number} id - Lesson ID
   * @param {Object} lessonData - Updated lesson data
   * @param {File} [lessonData.pdf_file] - New PDF file (optional)
   * @returns {Promise<Object>} Updated lesson
   */
  updateLesson: async (id, lessonData) => {
    const formData = new FormData();

    // Append all lesson fields
    Object.keys(lessonData).forEach(key => {
      if (lessonData[key] !== null && lessonData[key] !== undefined) {
        formData.append(key, lessonData[key]);
      }
    });

    const response = await api.put(`/lessons/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Delete a lesson (Admin only)
   * @param {number} id - Lesson ID
   * @returns {Promise<void>}
   */
  deleteLesson: async (id) => {
    await api.delete(`/lessons/${id}/`);
  },

  /**
   * Reorder lessons in bulk (Admin only)
   * @param {Array<number>} lessonIds - Array of lesson IDs in desired order
   * @returns {Promise<Object>} Success message
   */
  reorderLessons: async (lessonIds) => {
    const response = await api.post('/lessons/reorder/', { lesson_ids: lessonIds });
    return response.data;
  },

  // ===== PDF ACCESS =====

  /**
   * Get protected PDF URL for a lesson
   * Note: This endpoint requires authentication and checks enrollment status
   * @param {number} lessonId - Lesson ID
   * @returns {string} Full URL to protected PDF endpoint
   */
  getLessonPdfUrl: (lessonId) => {
    const baseURL = api.defaults.baseURL || 'http://localhost:8000/api';
    return `${baseURL}/lessons/${lessonId}/view-pdf/`;
  }
};

export default courseService;
